import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAdminProduct, listAdminProducts } from '@/services/products';
import type { Product } from '@/domain/types';

const typeColors: Record<string, string> = {
  plugin: 'bg-brand-50 text-brand-700',
  theme: 'bg-amber-50 text-amber-700',
  template: 'bg-sky-50 text-sky-700',
  addon: 'bg-violet-50 text-violet-700',
  asset: 'bg-rose-50 text-rose-700',
  app: 'bg-teal-50 text-teal-700',
};

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    async function loadProducts() {
      try {
        setLoading(true);
        setError('');
        const nextProducts = await listAdminProducts();

        if (alive) {
          setProducts(nextProducts);
        }
      } catch (err) {
        if (alive) {
          setError(err instanceof Error ? err.message : 'Unable to load products');
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      alive = false;
    };
  }, []);

  const filtered = products.filter((p) => {
    if (filter !== 'all' && p.status !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this product?')) {
      return;
    }

    try {
      await deleteAdminProduct(id);
      setProducts((current) => current.filter((product) => product.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete product');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Products</h1>
          <p className="text-sm text-surface-500 mt-1">Manage your product catalog.</p>
        </div>
        <button
          onClick={() => navigate('/admin/products/new')}
          className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line mr-1" /> Add Product
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-2">
          {['all', 'published', 'draft', 'archived'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filter === f ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        {error && (
          <div className="m-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Product</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Type</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Price From</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Sales</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-surface-500">
                    Loading products...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-surface-500">
                    No products found.
                  </td>
                </tr>
              )}
              {filtered.map((product) => (
                <tr key={product.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-surface-100 overflow-hidden flex items-center justify-center">
                        {product.thumbnail ? (
                          <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-box-3-line text-surface-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-surface-900">{product.name}</p>
                        <p className="text-xs text-surface-500">v{product.version}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(product.typeNames?.length ? product.typeNames : [product.type]).map((t) => (
                        <span key={t} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${typeColors[t.toLowerCase()] || 'bg-surface-100 text-surface-600'}`}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-semibold text-surface-900">${product.priceFrom}</td>
                  <td className="px-5 py-3 text-surface-600">{product.sales.toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${product.status === 'published' ? 'bg-green-50 text-green-700' : product.status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-surface-100 text-surface-600'}`}>
                      {product.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/admin/products/${product.id}/edit`)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                        <i className="ri-pencil-line" />
                      </button>
                      <button onClick={() => handleDelete(product.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-red-500">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
